/**
 * Training API client — typed wrappers around /cima/training/* endpoints.
 * Mirrors the shape returned by backend/cima/src/routes/training.ts.
 */
import { API_BASE, getApiHeaders } from './apiClient';

// ── Types ────────────────────────────────────────────────────────────────

export interface QuizOption {
  text: string;
  /** Stable identifier for the option ("a", "b", "c"…). */
  value: string;
}

export type QuestionType = 'single_choice' | 'multiple_choice' | 'drag_order' | 'drag_match' | 'short_answer';

export interface TrainingQuestion {
  id: number;
  moduleId: number;
  questionText: string;
  questionType: QuestionType;
  options: QuizOption[];
  /** Right-side targets for drag_match type. */
  matchOptions?: QuizOption[];
  /** Only present for admin/manager (hidden from staff). */
  correctAnswers?: any;
  explanation?: string | null;
  orderIndex: number;
}

export interface TrainingModule {
  id: number;
  title: string;
  description: string | null;
  videoUrl: string | null;
  videoDurationSeconds: number | null;
  /** Optional image shown on the module card. Falls back to the default
   *  gradient + shield icon when absent. */
  thumbnailUrl?: string | null;
  passingScore: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TrainingAssignment {
  id: number;
  moduleId: number;
  staffId: number;
  assignedBy: number | null;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE';
  videoProgressPercent: number;
  bestQuizScore: number | null;
  attemptsCount: number;
  deadlineAt: string;
  startedAt: string | null;
  completedAt: string | null;
  // Joined fields (when present)
  moduleTitle?: string;
  moduleDescription?: string | null;
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  passingScore?: number;
  staffName?: string;
  staffEmail?: string;
}

export interface AttemptResultDetail {
  questionId: number;
  correct: boolean;
  expected: string[];
  given: string[];
  explanation?: string | null;
}

export interface AttemptResult {
  score: number;
  total: number;
  correct: number;
  passed: boolean;
  passingScore: number;
  results: AttemptResultDetail[];
  /** Present when this completion just unlocked a 3/5/10/15+ modules milestone. */
  newCertificate?: { id: number; milestone: number; certificateCode: string } | null;
}

// ── Certificates ─────────────────────────────────────────────────────────

export interface TrainingCertificate {
  id: number;
  milestone: number;
  modulesCompletedCount: number;
  certificateCode: string;
  issuedAt: string;
}

export interface CertificateListResponse {
  certificates: TrainingCertificate[];
  /** How many modules the user has completed in total. */
  completed: number;
  /** Next milestone threshold (e.g. 3, 5, 10, 15, 20 …). */
  nextMilestone: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...getApiHeaders(), ...(init?.headers || {}) },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as T;
}

// ── Modules ──────────────────────────────────────────────────────────────

export async function listModules(includeInactive = false): Promise<TrainingModule[]> {
  const r = await api<{ modules: TrainingModule[] }>(
    `/cima/training/modules${includeInactive ? '?includeInactive=true' : ''}`,
  );
  return r.modules;
}

export async function getModule(id: number): Promise<{ module: TrainingModule; questions: TrainingQuestion[] }> {
  return api(`/cima/training/modules/${id}`);
}

export async function createModule(data: {
  title: string;
  description?: string;
  videoUrl?: string;
  videoDurationSeconds?: number;
  thumbnailUrl?: string | null;
  passingScore?: number;
  isDefault?: boolean;
}): Promise<TrainingModule> {
  const r = await api<{ module: TrainingModule }>('/cima/training/modules', {
    method: 'POST', body: JSON.stringify(data),
  });
  return r.module;
}

export async function updateModule(id: number, data: Partial<{
  title: string;
  description: string | null;
  videoUrl: string | null;
  videoDurationSeconds: number | null;
  thumbnailUrl: string | null;
  passingScore: number;
  isDefault: boolean;
  isActive: boolean;
}>): Promise<TrainingModule> {
  const r = await api<{ module: TrainingModule }>(`/cima/training/modules/${id}`, {
    method: 'PATCH', body: JSON.stringify(data),
  });
  return r.module;
}

export async function deleteModule(id: number): Promise<void> {
  await api(`/cima/training/modules/${id}`, { method: 'DELETE' });
}

// ── Quiz questions ───────────────────────────────────────────────────────

export async function createQuestion(
  moduleId: number,
  data: {
    questionText: string;
    /** Defaults to 'single_choice' on the backend — pass explicitly when
     *  saving AI-generated questions so drag_match / drag_order / multiple_choice
     *  etc. don't silently collapse to a radio-button UI. */
    questionType?: QuestionType;
    options: QuizOption[];
    /** Right-side targets for drag_match questions — stored alongside
     *  `options` on the backend as {items, targets}. */
    matchOptions?: QuizOption[];
    /** Shape depends on questionType:
     *   - single_choice     : ["a"]
     *   - multiple_choice   : ["a","c"]
     *   - drag_order        : ["b","a","d","c"]  (correct sequence)
     *   - drag_match        : {"l1":"r1", ...}   (correct pairings)
     *   - short_answer      : ["keyword1","keyword2"]
     */
    correctAnswers: any;
    explanation?: string;
    orderIndex?: number;
  },
): Promise<TrainingQuestion> {
  const r = await api<{ question: TrainingQuestion }>(`/cima/training/modules/${moduleId}/questions`, {
    method: 'POST', body: JSON.stringify(data),
  });
  return r.question;
}

export async function updateQuestion(
  moduleId: number,
  questionId: number,
  data: Partial<{
    questionText: string;
    /** Pass when changing the type via the editor's type picker — backend
     *  also re-stores `options` as {items, targets} when type=drag_match. */
    questionType: QuestionType;
    options: QuizOption[];
    /** Right-side targets for drag_match. */
    matchOptions: QuizOption[];
    /** See createQuestion — shape depends on questionType. */
    correctAnswers: any;
    explanation: string;
    orderIndex: number;
  }>,
): Promise<TrainingQuestion> {
  const r = await api<{ question: TrainingQuestion }>(`/cima/training/modules/${moduleId}/questions/${questionId}`, {
    method: 'PATCH', body: JSON.stringify(data),
  });
  return r.question;
}

export async function deleteQuestion(moduleId: number, questionId: number): Promise<void> {
  await api(`/cima/training/modules/${moduleId}/questions/${questionId}`, { method: 'DELETE' });
}

// ── Video upload ─────────────────────────────────────────────────────────

/** Step 1: ask backend for an S3 presigned URL.
 *  Step 2: PUT the file directly to that URL with the right Content-Type. */
export async function getVideoUploadUrl(moduleId: number, filename: string, contentType: string): Promise<{
  uploadUrl: string;
  publicUrl: string;
  key: string;
}> {
  return api(`/cima/training/modules/${moduleId}/video-upload-url`, {
    method: 'POST',
    body: JSON.stringify({ filename, contentType }),
  });
}

/** Convenience: upload a File object end-to-end. Returns the public URL. */
export async function uploadVideo(moduleId: number, file: File, onProgress?: (pct: number) => void): Promise<string> {
  const { uploadUrl, publicUrl } = await getVideoUploadUrl(moduleId, file.name, file.type || 'video/mp4');
  // Use XHR so we can report upload progress (fetch doesn't expose it natively)
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(publicUrl);
      else reject(new Error(`Upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

/** Thumbnail upload — same two-step pattern as video. Accepts any image/*
 *  content type; backend gates this server-side. */
export async function getThumbnailUploadUrl(moduleId: number, filename: string, contentType: string): Promise<{
  uploadUrl: string;
  publicUrl: string;
  key: string;
}> {
  return api(`/cima/training/modules/${moduleId}/thumbnail-upload-url`, {
    method: 'POST',
    body: JSON.stringify({ filename, contentType }),
  });
}

export async function uploadThumbnail(moduleId: number, file: File, onProgress?: (pct: number) => void): Promise<string> {
  const { uploadUrl, publicUrl } = await getThumbnailUploadUrl(moduleId, file.name, file.type || 'image/png');
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'image/png');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(publicUrl);
      else reject(new Error(`Upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

// ── Assignments ──────────────────────────────────────────────────────────

export async function listMyAssignments(): Promise<TrainingAssignment[]> {
  const r = await api<{ assignments: TrainingAssignment[] }>('/cima/training/assignments/me');
  return r.assignments;
}

export async function listAllAssignments(): Promise<TrainingAssignment[]> {
  const r = await api<{ assignments: TrainingAssignment[] }>('/cima/training/assignments');
  return r.assignments;
}

export async function assignModule(moduleId: number, staffIds: number[], deadlineAt?: string): Promise<{
  assigned: number;
  total: number;
}> {
  return api('/cima/training/assignments', {
    method: 'POST',
    body: JSON.stringify({ moduleId, staffIds, deadlineAt }),
  });
}

// ── Attempt flow (staff-side) ────────────────────────────────────────────

export async function startAssignment(id: number): Promise<{ started: boolean }> {
  return api(`/cima/training/assignments/${id}/start`, { method: 'POST' });
}

export async function saveVideoProgress(id: number, videoProgressPercent: number): Promise<void> {
  await api(`/cima/training/assignments/${id}/progress`, {
    method: 'PATCH',
    body: JSON.stringify({ videoProgressPercent }),
  });
}

export async function startAttempt(assignmentId: number): Promise<{
  attemptId: number;
  questions: TrainingQuestion[];
}> {
  return api(`/cima/training/assignments/${assignmentId}/attempt`, { method: 'POST' });
}

export async function submitAttempt(
  attemptId: number,
  responses: { questionId: number; selectedAnswers: string[] }[],
  timeTakenSeconds?: number,
): Promise<AttemptResult> {
  return api(`/cima/training/attempts/${attemptId}/submit`, {
    method: 'POST',
    body: JSON.stringify({ responses, timeTakenSeconds }),
  });
}

// ── AI quiz generation ───────────────────────────────────────────────

/** Send training content (text) to the AI and get back a mix of question types.
 *  Returns un-saved questions — admin reviews and saves individually. */
export async function generateQuizFromContent(
  moduleId: number,
  content: string,
  questionCount = 6,
): Promise<TrainingQuestion[]> {
  const r = await api<{ questions: TrainingQuestion[] }>(
    `/cima/training/modules/${moduleId}/generate-quiz`,
    { method: 'POST', body: JSON.stringify({ content, questionCount }) },
  );
  return r.questions;
}

// ── Certificates ─────────────────────────────────────────────────────────

export async function listMyCertificates(): Promise<CertificateListResponse> {
  return api<CertificateListResponse>(`/cima/training/certificates/me`);
}

/** URL for the printable HTML certificate (used by openCertificate). */
export function certificateDownloadUrl(id: number): string {
  return `${API_BASE}/cima/training/certificates/${id}/download`;
}

/**
 * Fetch the printable HTML certificate with the app's auth headers, then open
 * it in a new tab via a blob URL. Can't use `window.open(url)` directly because
 * the browser would not attach our `X-User-Email` header on a plain navigation.
 */
export async function openCertificate(id: number): Promise<void> {
  const res = await fetch(certificateDownloadUrl(id), { headers: getApiHeaders() });
  if (!res.ok) throw new Error(`Could not load certificate (HTTP ${res.status})`);
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after a minute — gives the new tab time to fully load.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── Tutor chat ───────────────────────────────────────────────────────────

export async function chatWithTutor(
  assignmentId: number,
  message: string,
  history: { role: 'user' | 'assistant'; content: string }[] = [],
): Promise<string> {
  const r = await api<{ reply: string }>(`/cima/training/chat/${assignmentId}`, {
    method: 'POST',
    body: JSON.stringify({ message, history }),
  });
  return r.reply;
}
